import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { getServices } from "../services/serviceService";

const SERVICE_PREVIEW_LIMIT = 155;

const stripHtml = (html = "") => {
  if (!html) return "";

  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  const tempElement = document.createElement("div");
  tempElement.innerHTML = html;
  return (tempElement.textContent || tempElement.innerText || "")
    .replace(/\s+/g, " ")
    .trim();
};

const getPreviewText = (text, limit = SERVICE_PREVIEW_LIMIT) => {
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}...`;
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [hasFired, setHasFired] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);
  const location = useLocation();

  // Trigger fetch if visible
  useEffect(() => {
    if (hasFired) return;

    const targetId = "services-section";
    const immediateHashes = new Set([`#${targetId}`, "#main-banner-area"]);

    // Services overlap the hero section, so load them immediately when the
    // user lands on the top banner as well as on the services hash itself.
    if (!location.hash || immediateHashes.has(location.hash)) {
      setHasFired(true);
      return;
    }

    let observer = null;
    let timer = null;

    const startObserver = () => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setHasFired(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1, rootMargin: "0px" }
      );
      if (sectionRef.current) observer.observe(sectionRef.current);
    };

    // Delay observing when another hash is active so the browser can finish
    // the initial hash scroll before we start lazy-loading this section.
    timer = setTimeout(startObserver, 2000);

    return () => {
      if (timer) clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [hasFired, location.hash]);

  // fetch data
  useEffect(() => {
    if (!hasFired) return;

    const loadServices = async () => {
      try {
        const res = await getServices();

        let serviceData = [];

        // Handle various response formats
        if (res && res.data && Array.isArray(res.data)) {
          serviceData = res.data;
        } else if (Array.isArray(res)) {
          serviceData = res;
        } else if (res && res.aaData && Array.isArray(res.aaData)) {
          serviceData = res.aaData;
        }

        if (serviceData.length > 0) {
          setServices(serviceData);
        } else {
          console.warn("Service data is empty");
          setServices([]);
        }
      } catch (err) {
        console.error("Services API ERROR:", err);
      }
    };

    loadServices();
  }, [hasFired]);

  // owl carousel control
  useEffect(() => {
    // Only initialize if we have data and jQuery is available
    if (services.length > 0 && window.$ && sliderRef.current) {
      const $slider = window.$(sliderRef.current);

      const destroySlider = () => {
        if ($slider.data("owl.carousel")) {
          $slider.trigger("destroy.owl.carousel");
        }
      };

      const markVisualCenterOnSmallScreens = () => {
        // Keep existing `.owl-item.center .service-box` CSS behavior on tablet/mobile
        if (window.innerWidth >= 992) return;

        const $items = $slider.find(".owl-item");
        if ($items.length === 0) return;

        $items.removeClass("center");
        const $activeItems = $slider.find(".owl-item.active");
        if ($activeItems.length === 0) return;

        const middleActiveIndex = Math.floor(($activeItems.length - 1) / 2);
        $activeItems.eq(middleActiveIndex).addClass("center");
      };

      const refreshSliderLayout = () => {
        if (!$slider.data("owl.carousel")) return;
        $slider.trigger("refresh.owl.carousel");
        markVisualCenterOnSmallScreens();
      };

      destroySlider();

      $slider.on(
        "initialized.owl.carousel translated.owl.carousel resized.owl.carousel refreshed.owl.carousel",
        markVisualCenterOnSmallScreens
      );

      $slider.owlCarousel({
        items: 3,
        loop: true,
        center: false,
        stagePadding: 0,
        margin: 0,
        autoplay: true,
        autoplayTimeout: 3000,
        smartSpeed: 600,
        nav: false,
        dots: true,
        responsive: {
          0: { items: 1, center: false },
          640: { items: 3, center: false },
          992: { items: 3, center: true },
        },
      });

      // Some hard refreshes land before the hero/layout has fully settled.
      // Re-refresh after paint/load so the services overlap renders immediately.
      const refreshOnNextPaint = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(refreshSliderLayout);
        });
      };

      const delayedRefreshId = window.setTimeout(refreshOnNextPaint, 250);

      window.addEventListener("load", refreshOnNextPaint);
      window.addEventListener("resize", refreshOnNextPaint);

      // Ensure center highlight is applied immediately after init on smaller screens
      markVisualCenterOnSmallScreens();

      // Fix for "Read more" button reliability on clones
      const handleSliderClick = (e) => {
        const btn = e.target.closest(".service-read-more");
        if (btn) {
          const serviceId = btn.getAttribute("data-service-id");
          const foundService = services.find(s => (s.id || s.index).toString() === serviceId.toString());
          if (foundService) {
            setActiveService(foundService);
          }
        }
      };

      $slider.on("click", handleSliderClick);

      // Cleanup on unmount
      return () => {
        window.clearTimeout(delayedRefreshId);
        window.removeEventListener("load", refreshOnNextPaint);
        window.removeEventListener("resize", refreshOnNextPaint);
        $slider.off(
          "initialized.owl.carousel translated.owl.carousel resized.owl.carousel refreshed.owl.carousel",
          markVisualCenterOnSmallScreens
        );
        $slider.off("click", handleSliderClick);
        destroySlider();
      };
    }
  }, [services]);

  useEffect(() => {
    if (!activeService) return undefined;

    const scrollY = window.scrollY;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalHtmlScrollBehavior = document.documentElement.style.scrollBehavior;
    const originalBodyScrollBehavior = document.body.style.scrollBehavior;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveService(null);
      }
    };

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      document.documentElement.style.scrollBehavior = "auto";
      document.body.style.scrollBehavior = "auto";
      window.removeEventListener("keydown", handleKeyDown);
      window.scrollTo(0, scrollY);
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = originalHtmlScrollBehavior;
        document.body.style.scrollBehavior = originalBodyScrollBehavior;
      });
    };
  }, [activeService]);

  // Stop/Start autoplay when modal is open
  useEffect(() => {
    if (window.$ && sliderRef.current) {
      const $slider = window.$(sliderRef.current);
      if (activeService) {
        $slider.trigger("stop.owl.autoplay");
      } else {
        $slider.trigger("play.owl.autoplay");
      }
    }
  }, [activeService]);

  return (
    <div ref={sectionRef} id="services-section" style={{ minHeight: services.length > 0 ? 'auto' : '500px' }}>
      {services.length > 0 && (
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div id="services-slider" className="owl-carousel" ref={sliderRef}>
                {services.map((service, index) => {
                  const plainDescription = stripHtml(service.description || "");
                  const hasLongDescription = plainDescription.length > SERVICE_PREVIEW_LIMIT;
                  const previewText = getPreviewText(plainDescription);

                  return (
                    <div className="item" key={service.id || index}>
                      <div className="service-box">
                        <span className="service-icon-wrap">
                          <i className={service.icon || 'fa fa-cogs'}></i>
                        </span>
                        <h4>
                          <span>{service.name || service.title}</span>
                        </h4>
                        <div className="service-box-content">
                          <p className={`service-box-preview ${hasLongDescription ? 'is-clamped' : ''}`}>{previewText}</p>
                          {hasLongDescription && (
                            <button
                              type="button"
                              className="service-read-more"
                              data-service-id={service.id || index}
                              onClick={() => setActiveService(service)}
                            >
                              Read more
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {activeService && typeof document !== "undefined" && createPortal(
        <div
          className="service-modal-backdrop"
          role="presentation"
          onClick={() => setActiveService(null)}
        >
          <div
            className="service-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`service-modal-title-${activeService.id || activeService.name || activeService.title || "service"}`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="service-modal-close"
              aria-label="Close service details"
              onClick={() => setActiveService(null)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <div className="service-modal-header">
              <span className="service-modal-icon">
                <i className={activeService.icon || 'fa fa-cogs'}></i>
              </span>
              <h4
                id={`service-modal-title-${activeService.id || activeService.name || activeService.title || "service"}`}
                className="service-modal-title"
              >
                {activeService.name || activeService.title}
              </h4>
            </div>
            <div
              className="service-modal-body"
              dangerouslySetInnerHTML={{ __html: activeService.description || "" }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Services;
